import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

const ANIMATION_OUT_MS = 300;
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Side = 'right';

interface InShellPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: Side;
  className?: string;
  overlayClassName?: string;
  hideCloseButton?: boolean;
  closeLabel?: string;
  children?: React.ReactNode;
  'data-testid'?: string;
  /**
   * If false, ESC and outside-click do not close the panel.
   * Default: true.
   */
  dismissable?: boolean;
}

/**
 * In-shell overlay panel — anchored inside the nearest positioned ancestor
 * (typically `#page-wrapper`, which AppShell renders with `position: relative;
 * overflow: hidden`). Use INSTEAD of `Sheet` when the panel should sit below
 * the global top navigation rather than over it.
 *
 * Why this exists (vs `Sheet`):
 *  - `Sheet` portals to `document.body` and renders at body z-50, so any
 *    nested dialog / dropdown / tooltip has to manually elevate above z-50
 *    via z-[60]/z-[70]/z-[80] hacks.
 *  - `InShellPanel` renders inline with `position: absolute` inside its
 *    nearest positioned ancestor and uses tiny *local* z-indexes (z-10 scrim,
 *    z-20 content). Anything portaled to `document.body` (the Radix default
 *    for `Dialog`, `AlertDialog`, `Popover`, `DropdownMenu`, `Tooltip`)
 *    naturally outranks it via DOM order — no manual z-index work at child
 *    call sites.
 *
 * Animation classes are copied verbatim from `Sheet` so motion is identical.
 *
 * NOT a drop-in replacement for `Sheet` in arbitrary contexts. Two intentional
 * choices in the codebase:
 *  - `Sheet`         — body-portaled, full-viewport, used when the panel
 *                      should overlay the navbar too.
 *  - `InShellPanel`  — page-scoped, sits below the navbar, used when the
 *                      panel should overlay page content but leave the
 *                      navbar interactive.
 */
const InShellPanel = React.forwardRef<HTMLDivElement, InShellPanelProps>(
  (
    {
      open,
      onOpenChange,
      side = 'right',
      className,
      overlayClassName,
      hideCloseButton,
      closeLabel = 'Close',
      children,
      dismissable = true,
      ...props
    },
    forwardedRef,
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(forwardedRef, () => contentRef.current as HTMLDivElement);

    const lastFocusedRef = React.useRef<HTMLElement | null>(null);
    const [shouldRender, setShouldRender] = React.useState(open);

    React.useEffect(() => {
      if (open) {
        setShouldRender(true);
        return;
      }
      const t = window.setTimeout(() => setShouldRender(false), ANIMATION_OUT_MS);
      return () => window.clearTimeout(t);
    }, [open]);

    React.useEffect(() => {
      if (!open) return;
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      const focusFirst = () => {
        const el = contentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        el?.focus();
      };
      const raf = window.requestAnimationFrame(focusFirst);
      return () => {
        window.cancelAnimationFrame(raf);
        lastFocusedRef.current?.focus();
      };
    }, [open]);

    React.useEffect(() => {
      if (!open) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (!dismissable) return;
          e.preventDefault();
          onOpenChange(false);
          return;
        }
        if (e.key === 'Tab' && contentRef.current) {
          const focusables = Array.from(
            contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
          ).filter((el) => el.offsetParent !== null);
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, dismissable, onOpenChange]);

    if (!shouldRender) return null;

    const state = open ? 'open' : 'closed';

    return (
      <>
        <div
          aria-hidden="true"
          data-state={state}
          onClick={() => {
            if (dismissable) onOpenChange(false);
          }}
          className={cn(
            'absolute inset-0 z-10 bg-dialog-overlay/50',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            overlayClassName,
          )}
        />
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          data-state={state}
          data-testid={props['data-testid']}
          className={cn(
            'absolute z-20 gap-4 bg-background p-6 shadow-sm transition ease-in-out',
            'data-[state=closed]:duration-300 data-[state=open]:duration-500',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            side === 'right' &&
              'inset-y-0 right-0 w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:w-[40vw] sm:max-w-[40vw] overflow-y-auto',
            className,
          )}
        >
          {!hideCloseButton && (
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={closeLabel}
              className="absolute right-4 top-4 cursor-pointer rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{closeLabel}</span>
            </button>
          )}
          {children}
        </div>
      </>
    );
  },
);
InShellPanel.displayName = 'InShellPanel';

const InShellPanelHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
InShellPanelHeader.displayName = 'InShellPanelHeader';

const InShellPanelFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
InShellPanelFooter.displayName = 'InShellPanelFooter';

const InShellPanelTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...props} />
));
InShellPanelTitle.displayName = 'InShellPanelTitle';

const InShellPanelDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
InShellPanelDescription.displayName = 'InShellPanelDescription';

export {
  InShellPanel,
  InShellPanelHeader,
  InShellPanelFooter,
  InShellPanelTitle,
  InShellPanelDescription,
};
export type { InShellPanelProps };
